// ============================================
// FILE: src/features/locker-controllers/components/LockerControllerActions.tsx
// ============================================

import React, { useState } from 'react';
import {
  usePermissions,
  useTranslate,
  useRedirect,
} from 'react-admin';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
} from '@mui/icons-material';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';

interface LockerControllerActionsProps {
  record: any;
}

const LockerControllerActions: React.FC<LockerControllerActionsProps> = ({ record }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const redirect = useRedirect();
  
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  const role = permissions || localStorage.getItem('role');
  const canEdit = ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'].includes(role);
  const canDelete = ['SuperAdmin', 'GroupAdmin'].includes(role);

  const handleEdit = () => {
    handleClose();
    redirect('edit', 'locker-controllers', record.id);
  };

  const handleDeleteClick = () => {
    handleClose();
    setShowDeleteDialog(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteDialog(false);
  };

  const handleDeleteConfirm = () => {
    setShowDeleteDialog(false);
  };

  const deleteMessage = record.lockersCount && record.lockersCount > 0
    ? translate(
        'resources.locker-controllers.dialogs.delete_warning_with_lockers',
        { 
          name: record.name, 
          count: record.lockersCount,
          smart_count: record.lockersCount 
        }
      )
    : translate(
        'resources.locker-controllers.dialogs.delete_message',
        { name: record.name }
      );

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={translate('ra.action.menu', 'Más opciones')}>
          <IconButton
            size={'small'}
            onClick={handleMenuOpen}
            sx={{ color: 'action.active' }}
          >
            <MoreVert fontSize={'small'} />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose()}
        onClick={(e) => e.stopPropagation()}
      >
        {canEdit && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('ra.action.edit')}</ListItemText>
          </MenuItem>
        )}
        
        {canDelete && (
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('ra.action.delete')}</ListItemText>
          </MenuItem>
        )}

      
      </Menu>

      {showDeleteDialog && (
        <BaseDeleteDialog
          open={showDeleteDialog}
          onClose={handleDeleteClose}
          onConfirm={handleDeleteConfirm}
          resource="locker-controllers"
          recordId={record.id}
          record={record}
          title={translate('resources.locker-controllers.dialogs.delete_title', 'Eliminar Controlador')}
          message={deleteMessage}
          warningMessage={
            record.lockersCount && record.lockersCount > 0
              ? translate(
                  'resources.locker-controllers.dialogs.delete_cascade_warning',
                  'Esta acción afectará a las taquillas asociadas.'
                )
              : undefined
          }
        />
      )}
    </>
  );
};

export default LockerControllerActions;